/**
 * Help content data structure for in-app user guides
 * Comprehensive tutorials covering navigation, UI elements, dropdowns, and settings
 */

export type HelpCategory = 'getting-started' | 'scenarios' | 'projections' | 'settings' | 'navigation' | 'ui-elements';

export interface HelpArticle {
  id: string;
  title: string;
  category: HelpCategory;
  content: string; // Markdown content
  keywords: string[];
  relatedArticles: string[];
  quickLinks?: {
    label: string;
    href: string;
  }[];
}

export const helpArticles: HelpArticle[] = [
  {
    id: 'navigation-guide',
    title: 'Navigation Guide',
    category: 'navigation',
    content: `# Navigation Guide

## Overview

DT DebtCast uses a sidebar navigation system for easy access to all features. This guide explains how to navigate the application.

## Sidebar Navigation

The **sidebar** on the left side of the screen contains the main navigation:

- **Home**: Returns to the dashboard overview
- **Debt Scenario Analysis**: Access scenario analysis features
- **Stochastic Projection**: Access stochastic projection features

### Using the Sidebar

1. **Click any menu item** to navigate to that section
2. **The active page** is highlighted in the sidebar
3. **Collapse the sidebar** by clicking the hamburger menu (☰) in the top-left corner
4. **Expand the sidebar** by clicking the menu icon again

## Top Navigation Bar

The **top navigation bar** contains:

- **Sidebar Toggle**: Show/hide the sidebar
- **Status Indicators**: 
  - Dev Mode indicator (development only)
  - Data source indicator
  - Databricks connection status
  - Cache status
- **Help Button** (?) : Access help and tutorials
- **Apps Menu**: Access other DevTech applications
- **User Menu**: Account settings and logout

## Page Navigation

### Dashboard (Home)

The dashboard provides an overview of available modules. Click on any module card to navigate to that section.

### Scenario Analysis Page

Navigate to **Debt Scenario Analysis** to:
- Select and compare scenarios
- View detailed tables
- Compare multiple scenarios side-by-side

### Stochastic Projection Page

Navigate to **Stochastic Projection** to:
- Generate Monte Carlo simulations
- View fan charts
- Configure projection parameters

## Tips

- Use the **breadcrumbs** at the top of pages to see your current location
- The **sidebar** stays visible while navigating between pages
- Use **browser back/forward** buttons to navigate history`,
    keywords: ['navigation', 'sidebar', 'menu', 'how to navigate', 'ui'],
    relatedArticles: ['ui-elements-guide', 'dropdowns-guide'],
  },
  {
    id: 'ui-elements-guide',
    title: 'UI Elements Guide',
    category: 'ui-elements',
    content: `# UI Elements Guide

## Overview

This guide explains the various UI elements you'll encounter in DT DebtCast and how to use them.

## Common UI Components

### Buttons

- **Primary Buttons**: Blue buttons for main actions (e.g., "Generate Projection", "Apply Changes")
- **Secondary Buttons**: Outlined buttons for secondary actions
- **Ghost Buttons**: Minimal buttons for less important actions
- **Icon Buttons**: Buttons with icons only (e.g., help button, close button)

### Cards

Cards contain related content:
- **Header**: Title and description
- **Content**: Main information or controls
- **Actions**: Buttons or links at the bottom

### Tabs

Tabs organize content into sections:
- **Scenario Detail**: View individual scenario data
- **Scenario Comparison**: Compare multiple scenarios side-by-side

Click a tab to switch between views.

### Tables

Tables display structured data:
- **Sortable columns**: Click column headers to sort
- **Scrollable**: Use horizontal/vertical scroll for large tables
- **Full-screen view**: Click the maximize icon (⛶) to view tables in full screen

### Charts

Interactive charts visualize data:
- **Hover**: Hover over data points to see values
- **Zoom**: Some charts support zooming
- **Legend**: Click legend items to show/hide data series
- **Full-screen**: Click maximize icon for full-screen view

### Modals/Dialogs

Modals appear on top of the page for:
- **Settings**: Configure global parameters
- **Forms**: Add or edit items (e.g., borrowing instruments)
- **Confirmations**: Confirm important actions

Click outside the modal or press Escape to close.

## Status Indicators

The top navigation bar shows:
- **Data Source**: Where data is coming from
- **Connection Status**: Databricks connection health
- **Cache Status**: Whether caching is enabled

## Tooltips

Hover over elements with a **question mark (?)** or **info icon** to see helpful tooltips explaining what that element does.`,
    keywords: ['ui', 'elements', 'components', 'buttons', 'tables', 'charts', 'tooltips'],
    relatedArticles: ['navigation-guide', 'dropdowns-guide'],
  },
  {
    id: 'dropdowns-guide',
    title: 'Using Dropdowns',
    category: 'ui-elements',
    content: `# Using Dropdowns Guide

## Overview

Dropdowns (also called "select" menus) allow you to choose from a list of options. This guide explains how to use them throughout DT DebtCast.

## Common Dropdowns

### State Selection

**Location**: Global Settings Panel (top of page)

**Purpose**: Select which state's data to analyze

**How to Use**:
1. Click the dropdown labeled "Select State"
2. Choose from available states (currently Illinois, with more coming soon)
3. The selection affects all calculations and data displayed

**Note**: Currently limited to Illinois, but more states will be added.

### Scenario Selection

**Location**: Scenario Analysis page

**Purpose**: Choose which economic scenario to analyze

**Options**:
- **Baseline**: Standard economic projections without shocks
- **Shock Revenue**: Simulates changes in revenue assumptions
- **Shock Expenditure**: Simulates changes in expenditure assumptions
- **Shock Interest Rate**: Simulates changes in interest rate assumptions
- **Intergovernmental Revenue Shock**: Simulates changes in intergovernmental revenue

**How to Use**:
1. Click the "Scenario Selection" dropdown
2. Select a scenario
3. The page updates to show that scenario's data
4. Use the "Scenario Comparison" tab to compare multiple scenarios

### Indicator Selection

**Location**: Scenario Analysis page (in tables)

**Purpose**: Filter which indicators to display in tables

**Options**:
- **All**: Show all indicators
- **D**: Debt-to-GDP ratio only
- **GFN**: Gross Financing Needs only
- **RGDP_PCH**: Real GDP growth only
- **NGDP_PCH**: Nominal GDP growth only
- **EFFR**: Effective interest rate only

**How to Use**:
1. Click the indicator dropdown above the table
2. Select an indicator or "All"
3. The table updates to show only selected indicators

### Time Period Dropdowns

**Location**: Global Settings Panel

**Purpose**: Configure historical and projection time periods

**Options**: Various year ranges based on data availability

**How to Use**:
1. Open Global Settings Panel
2. Find the time period dropdowns
3. Select start and end years
4. Click "Apply Changes" to save

## Tips for Using Dropdowns

- **Click to open**: Click anywhere on the dropdown to see options
- **Type to search**: Some dropdowns support typing to filter options
- **Keyboard navigation**: Use arrow keys to navigate, Enter to select
- **Escape to close**: Press Escape to close without selecting
- **Multiple selections**: Some dropdowns support selecting multiple items (indicated by checkboxes)

## Dropdown States

- **Enabled**: Normal state, clickable
- **Disabled**: Grayed out, not clickable (indicates unavailable option)
- **Loading**: Shows spinner while loading options
- **Error**: Red border indicates invalid selection`,
    keywords: ['dropdown', 'select', 'menu', 'scenario', 'state', 'indicator'],
    relatedArticles: ['navigation-guide', 'settings-guide'],
  },
  {
    id: 'settings-guide',
    title: 'Global Settings Guide',
    category: 'settings',
    content: `# Global Settings Guide

## Overview

Global Settings control application-wide parameters that affect all calculations and visualizations. This guide explains each setting and why you might want to change it.

## Accessing Global Settings

Global Settings appear at the **top of most pages** in a card panel labeled "Global Parameters".

The panel is always visible and contains controls for state selection, shock parameters, and borrowing instruments.

## Key Settings

### State Selection

**What it does**: Selects which state's data to analyze

**Why change it**: Different states have different economic conditions and debt profiles

**How to change**: Use the "Select State" dropdown (currently limited to Illinois)

### Time Periods

#### Historical Begin Year

**What it does**: Sets the starting year for historical data analysis

**Why change it**: 
- Include more historical data for better statistical analysis
- Focus on recent years for more relevant trends
- Match specific analysis requirements

**Default**: Based on data availability

**Range**: Typically 2012-2023

#### Historical End Year

**What it does**: Sets the ending year for historical data

**Why change it**: 
- Use most recent available data
- Exclude outlier years
- Match projection start year

**Default**: Most recent available year

#### Projection Years

**What it does**: Number of years into the future to project

**Why change it**:
- Longer projections show long-term trends but may be less accurate
- Shorter projections are more accurate but show less future impact
- Match your analysis timeframe needs

**Default**: 10 years

**Range**: 1-10 years

### Debt Target

**What it does**: Target debt-to-GDP ratio for baseline projections

**Why change it**:
- Reflect policy goals
- Test different target scenarios
- Compare against current ratios

**Default**: Based on current state data

### Shock Parameters

**What they do**: Define how economic shocks affect projections

**Why change them**:
- Test different economic scenarios
- Model policy changes
- Assess risk under various conditions

**Shock Types**:
- **Revenue Shock**: Changes in revenue assumptions
- **Expenditure Shock**: Changes in expenditure assumptions
- **Interest Rate Shock**: Changes in interest rate assumptions
- **Intergovernmental Revenue Shock**: Changes in intergovernmental revenue

**How to configure**:
1. Click "Shock Parameters" button
2. Adjust shock parameters in the modal
3. Click "Apply Shock Parameters" to save changes

### Borrowing Instruments

**What they do**: Define types of debt instruments used in calculations

**Why change them**:
- Add new debt instruments
- Modify existing instrument parameters
- Remove unused instruments

**How to manage**:
1. Click "New Borrowing" to add instruments
2. Click trash icon (🗑️) to remove instruments
3. Modify parameters in the modal

**Instrument Parameters**:
- **Instrument**: Descriptive name (e.g., "5-Year Bond", "10-Year Bond")
- **Interest Rate**: Annual interest rate (%)
- **Maturity**: Years until repayment
- **Type**: Automatically set based on maturity (Short-Term ≤2 years, Long-Term >2 years)
- **Share**: Percentage allocation (0-100%) - must sum to 100% across all instruments
- **Currency**: Currency type (currently USD)

## Applying Changes

After modifying settings:

1. **Review your changes** in the settings panel
2. **Click "Apply Changes"** to save
3. **Wait for recalculation** - the page will update automatically
4. **Check for errors** - validation errors appear in red

## Resetting to Defaults

To reset all settings to defaults:

1. Click "Reset to Defaults" button
2. Confirm the action
3. Settings return to original values
4. Page recalculates with default settings

## Tips

- **Save frequently**: Apply changes regularly to avoid losing work
- **Test incrementally**: Change one setting at a time to understand impact
- **Use presets**: Some common configurations may be available
- **Document changes**: Note why you changed settings for future reference`,
    keywords: ['settings', 'global', 'parameters', 'configuration', 'time periods', 'shocks', 'borrowing'],
    relatedArticles: ['dropdowns-guide', 'scenario-analysis-overview'],
  },
  {
    id: 'scenario-analysis-tutorial',
    title: 'Scenario Analysis Tutorial',
    category: 'scenarios',
    content: `# Scenario Analysis Tutorial

## Step-by-Step Guide

### Step 1: Navigate to Scenario Analysis

1. Click **"Debt Scenario Analysis"** in the sidebar
2. Or click the **"Debt Scenario Analysis"** card on the dashboard

### Step 2: Select a Scenario

1. Find the **"Scenario Selection"** dropdown at the top of the page
2. Click the dropdown to see available scenarios:
   - Baseline
   - Shock Revenue
   - Shock Expenditure
   - Shock Interest Rate
   - Intergovernmental Revenue Shock
3. Select a scenario
4. The page updates to show that scenario's data

### Step 3: View Scenario Overview

After selecting a scenario, you'll see:
- **Scenario Description**: Explanation of what the scenario models
- **Key Parameters**: Important assumptions used

### Step 4: View Detailed Tables

1. Scroll down to the **"Key Macroeconomic and Debt Indicators"** table
2. Use the **indicator dropdown** to filter which indicators to view:
   - All indicators
   - Specific indicators (D, GFN, RGDP_PCH, etc.)
3. View **Actual** (historical) and **Projections** (future) data
4. Click the **maximize icon (⛶)** for full-screen table view

### Step 5: Compare Scenarios

1. Click the **"Scenario Comparison"** tab
2. View side-by-side comparison charts:
   - Debt-to-GDP ratios
   - Gross Financing Needs
   - Debt Service ratios
   - Debt-to-Revenue ratios
3. Compare how different scenarios affect these metrics

### Step 6: Configure Global Settings (Optional)

1. Locate the **"Global Parameters"** panel at the top of the page
2. Adjust settings as needed (see Global Settings Guide)
3. Click **"Apply Changes"**
4. Scenarios recalculate with new settings

## Tips

- **Start with Baseline**: Understand baseline projections before exploring shocks
- **Compare systematically**: Compare scenarios one at a time to understand differences
- **Use full-screen**: Maximize tables and charts for better visibility
- **Export data**: Use export features to save results for analysis`,
    keywords: ['scenario', 'tutorial', 'step by step', 'how to', 'analysis'],
    relatedArticles: ['scenario-analysis-overview', 'settings-guide'],
  },
  {
    id: 'stochastic-projections-tutorial',
    title: 'Stochastic Projections Tutorial',
    category: 'projections',
    content: `# Stochastic Projections Tutorial

## Step-by-Step Guide

### Step 1: Navigate to Stochastic Projections

1. Click **"Stochastic Projection"** in the sidebar
2. Or click the **"Stochastic Projection"** card on the dashboard

### Step 2: Configure Basic Parameters

In the **"Projection Parameters"** card:

1. **Target Debt-to-GDP (%)**: Set your target ratio
   - Default: Based on current state data
   - Why change: Reflect policy goals or test different targets

2. **Number of Simulations**: Set how many Monte Carlo paths to run
   - Default: 10,000
   - Range: 1,000 - 100,000
   - Why change: More simulations = more accurate but slower

3. **Projection Years**: Number of years to project
   - Default: 10 years
   - Range: 1-10 years
   - Why change: Match your analysis timeframe

### Step 3: Configure Advanced Settings (Optional)

Click **"Advanced Configuration"** to expand:

1. **Historical Begin/End Years**: Set historical data range
2. **Use Historical Covariance**: Toggle correlation between variables
3. **Percentiles**: Configure confidence intervals (20th, 35th, 50th, 65th, 80th)

### Step 4: Generate Projection

1. Review your parameter settings
2. Click **"Regenerate Projection"** button
3. Wait for calculation (may take a moment)
4. View results in the fan chart below

### Step 5: Interpret the Fan Chart

The fan chart shows:
- **Historical Data**: Solid line showing past debt-to-GDP ratios
- **Baseline Projection**: Dashed line showing expected path
- **Percentile Bands**: Shaded areas showing uncertainty:
  - Darker areas = higher probability
  - Wider bands = more uncertainty
- **Median (50th percentile)**: Most likely outcome

### Step 6: Understand Percentiles

Percentiles help assess risk:
- **20th percentile**: Optimistic scenario (20% chance of being lower)
- **50th percentile**: Median (most likely)
- **80th percentile**: Pessimistic scenario (20% chance of being higher)

### Step 7: Adjust and Re-run

1. Modify parameters based on what you see
2. Click **"Regenerate Projection"** again
3. Compare results
4. Use **"Reset to Defaults"** to start over

## Tips

- **Start with defaults**: Understand baseline before customizing
- **More simulations**: Use more simulations for important analyses
- **Interpret bands**: Wider bands indicate more uncertainty
- **Compare scenarios**: Generate multiple projections with different parameters`,
    keywords: ['stochastic', 'projections', 'tutorial', 'fan chart', 'monte carlo', 'percentiles'],
    relatedArticles: ['stochastic-projections-overview', 'settings-guide'],
  },
  {
    id: 'what-is-debtcast',
    title: 'What is DT DebtCast?',
    category: 'getting-started',
    content: `# What is DT DebtCast?

DT DebtCast is a debt sustainability analysis platform that helps state governments analyze debt sustainability under various economic scenarios.

## Key Features

- **Scenario Analysis**: Compare different economic scenarios
- **Stochastic Projections**: Monte Carlo simulations for risk assessment
- **Interactive Visualizations**: Charts and tables for data exploration
- **Real-time Data**: Live data from Databricks Unity Catalog

## What It Does

The platform processes financial data from Databricks through a Python FastAPI backend to a Next.js frontend. The system performs complex economic projections, debt sustainability analysis, and stochastic modeling for U.S. state governments.

## Who It's For

- **State Government Analysts**: Analyze debt sustainability
- **Policy Makers**: Understand impact of policy changes
- **Financial Analysts**: Assess risk and uncertainty
- **Researchers**: Study debt dynamics`,
    keywords: ['overview', 'introduction', 'what is', 'features', 'platform'],
    relatedArticles: ['navigation-guide', 'scenario-analysis-tutorial'],
  },
];

/**
 * Get help articles by category
 */
export function getHelpArticlesByCategory(category: HelpCategory): HelpArticle[] {
  return helpArticles.filter(article => article.category === category);
}

/**
 * Search help articles by keyword
 */
export function searchHelpArticles(query: string): HelpArticle[] {
  const lowerQuery = query.toLowerCase();
  return helpArticles.filter(article => 
    article.title.toLowerCase().includes(lowerQuery) ||
    article.content.toLowerCase().includes(lowerQuery) ||
    article.keywords.some(keyword => keyword.toLowerCase().includes(lowerQuery))
  );
}
